import {ThemeProvider} from "@mui/material/styles"
import GlobalStyles from "@mui/material/GlobalStyles"
import React from "react"
import {baseStyles} from "../assets/css/styles"
import Box from '@mui/material/Box'
import {theme} from "../assets/css/styles"
import {NavbarContextProvider} from "../contexts/navbar_context"
import {GlobalContextProvider} from "../contexts/global_context"
import CategoryLandingPage from "../pages/CategoryLandingPage"
import IndexPage from "../pages/IndexPage"
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom"
import EnterDetailsPage from "../pages/EnterDetailsPage"
import SelectPostsPage from "../pages/SelectPostsPage"
import CheckoutPage from "../pages/CheckoutPage"
import ScrollToTop from "./base/ScrollToTop"
import TermOfServicePage from "../pages/TermOfServicePage"
import PrivacyPolicyPage from "../pages/PrivacyPolicyPage"
import SupportPage from "../pages/SupportPage"
import RefundPolicyPage from "../pages/RefundPolicyPage"
import CategoryListAdminPage from '../pages/admin/category/CategoryListAdminPage';
import CategoryCreateUpdateAdminPage from '../pages/admin/category/CategoryCreateUpdateAdminPage';
import PackageListAdminPage from "../pages/admin/package/PackageListAdminPage";
import PackageCreateUpdateAdminPage from "../pages/admin/package/PackageCreateUpdateAdminPage";
import AdminLoginPage from "../pages/admin/users/AdminLoginPage";
import ProviderCreateUpdateAdminPage from "../pages/admin/provider/ProviderCreateUpdateAdminPage";
import ProviderListAdminPage from "../pages/admin/provider/ProviderListAdminPage";
import OrderListAdminPage from "../pages/admin/orders/OrderListAdminPage";
import SuccessPaymentPage from "../pages/SuccessPaymentPage";
import TemplateListAdminPage from "../pages/admin/template/TemplateListAdminPage";
import TemplateCreateUpdateAdminPage from "../pages/admin/template/TemplateCreateUpdateAdminPage";
import {AdminContextProvider} from "../contexts/admin_context";
import {GoogleReCaptchaProvider} from 'react-google-recaptcha-v3'
import {GOOGLE_RECAPTCHA_SITE_KEY} from "../data/config";
import TelegramChatListAdminPage from "../pages/admin/telegram_chat/TelegramChatListAdminPage";
import PrivateRoute from "./auth/PrivateRoute";
import TelegramChatCreateUpdateAdminPage from "../pages/admin/telegram_chat/TelegramChatCreateUpdateAdminPage";
import CustomerSignUpPage from "../pages/customer_auth/CustomerSignUpPage";
import LoginPage from "../pages/auth/LoginPage";
import ResetPasswordStep1Page from "../pages/auth/ResetPasswordStep1Page";
import ResetPasswordStep2Page from "../pages/auth/ResetPasswordStep2Page";
import CustomerDashboardPage from "../pages/customer_panel/main/CustomerDashboardPage";
import CustomerOrderListPage from "../pages/customer_panel/orders/CustomerOrderListPage";
import CustomerSubscriptionListPage from "../pages/customer_panel/subscriptions/CustomerSubscriptionListPage";
import CustomerBillingListPage from "../pages/customer_panel/billing/CustomerBillingListPage";
import CustomerSettingsPage from "../pages/customer_panel/settings/CustomerSettingsPage";
import SubscriptionListAdminPage from "../pages/admin/subscription/SubscriptionListAdminPage";
import SettingsAdminPage from "../pages/admin/settings/SettingsAdminPage";
import OrderDiscountListAdminPage from "../pages/admin/order_discount/OrderDiscountListAdminPage";
import OrderDiscountCreateUpdateAdminPage from "../pages/admin/order_discount/OrderDiscountCreateUpdateAdminPage";
import TopNotificationAdminPage from "../pages/admin/notifications/TopNotificationAdminPage";
import CategoryNotificationListAdminPage from "../pages/admin/notifications/CategoryNotificationListAdminPage";
import CategoryNotificationCreateUpdateAdminPage from "../pages/admin/notifications/CategoryNotificationCreateUpdateAdminPage";
import {Helmet} from "react-helmet";
import {BRAND_BASE_URL, BRAND_NAME} from "../data/config";
import {DEFAULT_META_DESCRIPTION} from "../data/head";


const DefaultSEO = () => {
  const location = useLocation()
  const canonical = `${BRAND_BASE_URL}${location.pathname}${location.search}`
  return (
    <Helmet>
      <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover"/>
      <meta name="theme-color" content="#D1C4E9"/>
      <meta name="theme-color" content="#D1C4E9" media="(prefers-color-scheme: light)"/>
      <meta name="theme-color" content="#D1C4E9" media="(prefers-color-scheme: dark)"/>
      <meta name="mobile-web-app-capable" content="yes"/>
      <meta name="apple-mobile-web-app-status-bar-style" content="default"/>
      <title>{BRAND_NAME}</title>
      <meta name="description" content={DEFAULT_META_DESCRIPTION}/>
      <meta name="robots" content="index,follow"/>
      <link rel="canonical" href={canonical}/>
      {/* Open Graph */}
      <meta property="og:type" content="website"/>
      <meta property="og:site_name" content={BRAND_NAME}/>
      <meta property="og:url" content={canonical}/>
      <meta property="og:title" content={BRAND_NAME}/>
      <meta property="og:description" content={DEFAULT_META_DESCRIPTION}/>
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image"/>
      <meta name="twitter:title" content={BRAND_NAME}/>
      <meta name="twitter:description" content={DEFAULT_META_DESCRIPTION}/>
    </Helmet>
  )
}

const App = () => {
  // to close menus after scrolling
  document.addEventListener('scroll', (e) => {
    try {
      document.querySelector('.MuiBackdrop-root').click()
    } catch (e) {
    }
  })

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={GOOGLE_RECAPTCHA_SITE_KEY}
      language={'en'}
    >
      <GlobalContextProvider>
        <NavbarContextProvider>
          <ThemeProvider theme={theme}>
            <GlobalStyles styles={{
              html: { backgroundColor: "#D1C4E9", margin: 0, padding: 0 },
              body: { backgroundColor: "#D1C4E9", margin: 0, padding: 0 }
            }} />

            <Box sx={baseStyles}>
              {/* public */}
              <Router>
                <DefaultSEO/>
                <ScrollToTop>
                  <Routes>
                    <Route
                      path="/"
                      element={<IndexPage/>}
                      exact
                    />

                    <Route
                      path="/categories/:categorySlug"
                      element={<CategoryLandingPage/>}
                      exact
                    />

                    <Route
                      path="/packages/:packageSlug"
                      element={<EnterDetailsPage/>}
                      exact
                    />

                    <Route
                      path="/order/select-posts"
                      element={<SelectPostsPage/>}
                      exact
                    />

                    <Route
                      path="/order/checkout/:orderUid"
                      element={<CheckoutPage/>}
                      exact
                    />

                    <Route
                      path="/order/success/:orderUid"
                      element={<SuccessPaymentPage/>}
                      exact
                    />

                    <Route
                      path="/terms"
                      element={<TermOfServicePage/>}
                      exact
                    />

                    <Route
                      path="/privacy-policy"
                      element={<PrivacyPolicyPage/>}
                      exact
                    />

                    <Route
                      path="/refund-policy"
                      element={<RefundPolicyPage/>}
                      exact
                    />

                    <Route
                      path="/support"
                      element={<SupportPage/>}
                      exact
                    />

                    {/* AUTHENTICATION */}
                    <Route
                      path="/signup"
                      element={<CustomerSignUpPage/>}
                      exact
                    />

                    <Route
                      path="/login"
                      element={<LoginPage/>}
                      exact
                    />

                    <Route
                      path="/reset-password-step-1"
                      element={<ResetPasswordStep1Page/>}
                      exact
                    />

                    <Route
                      path="/reset-password-step-2/:token"
                      element={<ResetPasswordStep2Page/>}
                      exact
                    />

                    {/* CUSTOMER DASHBOARD */}
                    <Route
                      path="/dashboard"
                      element={<CustomerDashboardPage/>}
                      exact
                    />

                    <Route
                      path="/dashboard/orders"
                      element={<CustomerOrderListPage/>}
                      exact
                    />

                    <Route
                      path="/dashboard/subscriptions"
                      element={<CustomerSubscriptionListPage/>}
                      exact
                    />
                    <Route
                      path="/dashboard/subscriptions/create"
                      element={<CustomerSubscriptionListPage/>}
                      exact
                    />
                    <Route
                      path="/dashboard/subscriptions/update/:uid"
                      element={<CustomerSubscriptionListPage/>}
                      exact
                    />

                    <Route
                      path="/dashboard/billing"
                      element={<CustomerBillingListPage/>}
                      exact
                    />
                    <Route
                      path="/dashboard/billing/create"
                      element={<CustomerBillingListPage/>}
                      exact
                    />
                    <Route
                      path="/dashboard/billing/update/:uid"
                      element={<CustomerBillingListPage/>}
                      exact
                    />

                    <Route
                      path="/dashboard/settings"
                      element={<CustomerSettingsPage/>}
                      exact
                    />
                  </Routes>
                </ScrollToTop>
              </Router>

              {/* admin */}
              <Router>
                <AdminContextProvider>
                  <Routes>
                    {/* login */}

                    {/* order */}
                    <Route
                      path="/admin/orders"
                      element={<PrivateRoute><OrderListAdminPage/></PrivateRoute>}
                      exact
                    />

                    <Route
                      path="/admin/subscriptions"
                      element={<PrivateRoute><SubscriptionListAdminPage/></PrivateRoute>}
                      exact
                    />

                    <Route
                      path="/admin/orders/details/:uid"
                      element={<PrivateRoute><OrderListAdminPage/></PrivateRoute>}
                      exact
                    />

                    {/* package */}
                    <Route
                      path="/admin/packages"
                      element={<PrivateRoute><PackageListAdminPage/></PrivateRoute>}
                      exact
                    />
                    <Route
                      path="/admin/packages/create"
                      element={<PrivateRoute><PackageCreateUpdateAdminPage/></PrivateRoute>}
                      exact
                    />
                    <Route
                      path="/admin/packages/update/:uid"
                      element={<PrivateRoute><PackageCreateUpdateAdminPage/></PrivateRoute>}
                      exact
                    />

                    {/* provider */}
                    <Route
                      path="/admin/providers"
                      element={<PrivateRoute><ProviderListAdminPage/></PrivateRoute>}
                      exact
                    />
                    <Route
                      path="/admin/providers/create"
                      element={<PrivateRoute><ProviderCreateUpdateAdminPage/></PrivateRoute>}
                      exact
                    />
                    <Route
                      path="/admin/providers/update/:uid"
                      element={<PrivateRoute><ProviderCreateUpdateAdminPage/></PrivateRoute>}
                      exact
                    />

                    {/* category */}
                    <Route
                      path="/admin/categories"
                      element={<PrivateRoute><CategoryListAdminPage/></PrivateRoute>}
                      exact
                    />
                    <Route
                      path="/admin/categories/create"
                      element={<PrivateRoute><CategoryCreateUpdateAdminPage/></PrivateRoute>}
                      exact
                    />
                    <Route
                      path="/admin/categories/update/:uid"
                      element={<PrivateRoute><CategoryCreateUpdateAdminPage/></PrivateRoute>}
                      exact
                    />

                    {/* template */}
                    <Route
                      path="/admin/templates"
                      element={<PrivateRoute><TemplateListAdminPage/></PrivateRoute>}
                      exact
                    />
                    <Route
                      path="/admin/templates/create"
                      element={<PrivateRoute><TemplateCreateUpdateAdminPage/></PrivateRoute>}
                      exact
                    />
                    <Route
                      path="/admin/templates/update/:uid"
                      element={<PrivateRoute><TemplateCreateUpdateAdminPage/></PrivateRoute>}
                      exact
                    />

                    {/* telegram chat */}
                    <Route
                      path="/admin/telegram-chats"
                      element={<PrivateRoute><TelegramChatListAdminPage/></PrivateRoute>}
                      exact
                    />
                    <Route
                      path="/admin/telegram-chats/create"
                      element={<PrivateRoute><TelegramChatCreateUpdateAdminPage/></PrivateRoute>}
                      exact
                    />
                    <Route
                      path="/admin/telegram-chats/update/:uid"
                      element={<PrivateRoute><TelegramChatCreateUpdateAdminPage/></PrivateRoute>}
                      exact
                    />

                    {/* settings */}
                    <Route
                      path="/admin/settings"
                      element={<PrivateRoute><SettingsAdminPage/></PrivateRoute>}
                      exact
                    />

                    {/* notifications */}
                    <Route
                      path="/admin/notifications/top"
                      element={<PrivateRoute><TopNotificationAdminPage/></PrivateRoute>}
                      exact
                    />
                    <Route
                      path="/admin/notifications/category-notifications"
                      element={<PrivateRoute><CategoryNotificationListAdminPage/></PrivateRoute>}
                      exact
                    />
                    <Route
                      path="/admin/notifications/category-notifications/create"
                      element={<PrivateRoute><CategoryNotificationCreateUpdateAdminPage/></PrivateRoute>}
                      exact
                    />
                    <Route
                      path="/admin/notifications/category-notifications/update/:uid"
                      element={<PrivateRoute><CategoryNotificationCreateUpdateAdminPage/></PrivateRoute>}
                      exact
                    />

                    {/* order discount */}
                    <Route
                      path="/admin/order-discounts"
                      element={<PrivateRoute><OrderDiscountListAdminPage/></PrivateRoute>}
                      exact
                    />
                    <Route
                      path="/admin/order-discounts/create"
                      element={<PrivateRoute><OrderDiscountCreateUpdateAdminPage/></PrivateRoute>}
                      exact
                    />
                    <Route
                      path="/admin/order-discounts/update/:uid"
                      element={<PrivateRoute><OrderDiscountCreateUpdateAdminPage/></PrivateRoute>}
                      exact
                    />
                  </Routes>
                </AdminContextProvider>
              </Router>
              {/* admin */}
            </Box>
          </ThemeProvider>
        </NavbarContextProvider>
      </GlobalContextProvider>
    </GoogleReCaptchaProvider>
  )
}

export default App
